import React, { useEffect, useState } from "react";
import axios from "axios";
import { FiArchive } from 'react-icons/fi';
import { IoMdUndo } from 'react-icons/io';
import { FaTrashAlt } from 'react-icons/fa';
import { IoArrowBack } from 'react-icons/io5';
import { MdArchive, MdDelete } from 'react-icons/md';

// If REACT_APP_API_URL is set to empty string, use relative URLs (same domain)
// Otherwise use the provided URL or default to localhost for development
const API_URL = process.env.REACT_APP_API_URL !== undefined 
  ? process.env.REACT_APP_API_URL 
  : "http://localhost:8000";

export default function ArchivedPosts({ token }) {
  const [posts, setPosts] = useState([]);
  const [deletedPosts, setDeletedPosts] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [now, setNow] = useState(Date.now());
  const [tab, setTab] = useState('archive'); // 'archive' or 'recycle'
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: 'single', post } | { type: 'bulk', ids: [] } | null
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    async function fetchArchivedPosts() {
      if (!token) {
        setError("Authentication token is missing. Please log in again.");
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError("");
        const res = await axios.get(`${API_URL}/getArchivedPosts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Handle different response formats
        if (res.data && res.data.posts) {
          setPosts(res.data.posts);
        } else if (Array.isArray(res.data)) {
          setPosts(res.data);
        } else {
          setPosts([]);
        }
      } catch (err) {
        console.error("Error fetching archived posts:", err);
        const errorMessage = err.response?.data?.message || err.message || "Failed to fetch archived posts";
        setError(errorMessage);
        // If it's an authentication error, suggest re-login
        if (err.response?.status === 400 || err.response?.status === 401) {
          console.error("Authentication error - token may be invalid or expired");
        }
      } finally {
        setLoading(false);
      }
    }
    fetchArchivedPosts();
  }, [token]);

  // Fetch deleted (trashed) posts when user views Recycle bin
  useEffect(() => {
    if (tab !== 'recycle') return;
    if (!token) {
      setError("Authentication token is missing. Please log in again.");
      setLoading(false);
      return;
    }
    
    let cancelled = false;
    async function fetchDeleted() {
      try {
        setLoading(true);
        setError("");
        const res = await axios.get(`${API_URL}/getDeletedPosts`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Handle different response formats
        if (!cancelled) {
          if (res.data && res.data.posts) {
            setDeletedPosts(res.data.posts);
          } else if (Array.isArray(res.data)) {
            setDeletedPosts(res.data);
          } else {
            setDeletedPosts([]);
          }
        }
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching deleted posts:", err);
          const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch deleted posts';
          setError(errorMessage);
          if (err.response?.status === 400 || err.response?.status === 401) {
            console.error("Authentication error - token may be invalid or expired");
          }
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchDeleted();
    return () => { cancelled = true; };
  }, [tab, token]);

  // Close any open item menu when user clicks outside
  useEffect(() => {
    const handler = (e) => {
      // if click is outside any menu button, close openMenuId
      setOpenMenuId(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  // keep a 'now' timestamp updated so time-based UI (days-left) refreshes without page reload (every 5 min to avoid unnecessary re-renders)
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    } else {
      window.location.href = '/';
    }
  };

  const handleConfirmPermanentDelete = async () => {
    if (!deleteConfirm) return;
    setIsDeleting(true);
    try {
      if (deleteConfirm.type === 'single') {
        await axios.delete(`${API_URL}/permanentDelete/${deleteConfirm.post._id}`, { headers: { Authorization: `Bearer ${token}` } });
        setDeletedPosts((prev) => prev.filter((p) => p._id !== deleteConfirm.post._id));
        setOpenMenuId(null);
      } else {
        await Promise.all(deleteConfirm.ids.map((id) => axios.delete(`${API_URL}/permanentDelete/${id}`, { headers: { Authorization: `Bearer ${token}` } })));
        setDeletedPosts((prev) => prev.filter((p) => !deleteConfirm.ids.includes(p._id)));
        setSelectedIds((prev) => prev.filter((id) => !deleteConfirm.ids.includes(id)));
      }
      setDeleteConfirm(null);
    } catch (err) {
      console.error(err);
      alert(deleteConfirm.type === 'single' ? 'Failed to delete permanently' : 'Failed to delete selected posts');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="archived-posts-page" style={{ padding: 16 }}>
      {/* Permanent delete confirmation modal */}
      {deleteConfirm && (
        <div
          className="permanent-delete-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: 16,
          }}
          onClick={() => !isDeleting && setDeleteConfirm(null)}
        >
          <div
            className="permanent-delete-modal"
            style={{
              background: 'var(--bg-primary, #fff)',
              color: 'var(--color-primary, #333)',
              borderRadius: 12,
              boxShadow: '0 12px 28px rgba(0,0,0,0.2)',
              maxWidth: 320,
              width: '100%',
              overflow: 'hidden',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--bg-third, #e4e6eb)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>
                {deleteConfirm.type === 'single' ? 'Confirm Deletion' : 'Permanently delete selected publications?'}
              </h3>
              <button
                type="button"
                aria-label="Close"
                onClick={() => !isDeleting && setDeleteConfirm(null)}
                style={{
                  margin: 0,
                  padding: 0,
                  width: 32,
                  height: 32,
                  border: 'none',
                  borderRadius: '50%',
                  background: 'transparent',
                  fontSize: 24,
                  lineHeight: 1,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                  color: 'inherit',
                }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              <p style={{ margin: 0, color: 'var(--color-secondary, #65676b)', fontSize: 15, lineHeight: 1.4 }}>
                {deleteConfirm.type === 'single'
                  ? 'Permanently delete this publication?'
                  : `Permanently delete ${deleteConfirm.ids.length} selected publication(s)? This cannot be undone.`}
              </p>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--bg-third, #e4e6eb)' }}>
              <button
                type="button"
                onClick={() => !isDeleting && setDeleteConfirm(null)}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #ccc',
                  background: '#f0f2f5',
                  color: '#333',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirmPermanentDelete}
                disabled={isDeleting}
                style={{
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 15,
                  fontWeight: 600,
                  cursor: isDeleting ? 'not-allowed' : 'pointer',
                }}
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      <button
        className="archived-posts-back-btn"
        onClick={handleBack}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '10px 16px',
          marginBottom: 20,
          background: '#fff',
          border: '1px solid #ddd',
          borderRadius: 8,
          cursor: 'pointer',
          fontSize: 14,
          fontWeight: 500,
          color: '#333',
          transition: 'all 0.2s ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}
        onMouseEnter={(e) => {
          e.target.style.background = '#f8f9fa';
          e.target.style.borderColor = '#bbb';
          e.target.style.transform = 'translateY(-1px)';
          e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.15)';
        }}
        onMouseLeave={(e) => {
          e.target.style.background = '#fff';
          e.target.style.borderColor = '#ddd';
          e.target.style.transform = 'translateY(0)';
          e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
        }}
      >
        <IoArrowBack size={16} />
        <span className="archived-back-text">Back</span>
      </button>

      <div className="archived-posts-layout" style={{ display: 'flex', gap: 24 }}>
        {/* Left sidebar */}
        <aside className="archived-posts-sidebar" style={{ width: 260, borderRadius: 12, background: '#fff', padding: 20, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid #f0f0f0' }}>
          <h3 style={{
            marginTop: 0,
            marginBottom: 20,
            fontSize: 18,
            fontWeight: 600,
            color: '#1a1a1a',
            letterSpacing: '-0.02em',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
          }}>
            Archive Management
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <button
              onClick={() => setTab('archive')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: 8,
                background: tab === 'archive' ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: tab === 'archive' ? 600 : 500,
                color: tab === 'archive' ? '#fff' : '#4a5568',
                transition: 'all 0.2s ease',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                boxShadow: tab === 'archive' ? '0 4px 12px rgba(102, 126, 234, 0.4)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (tab !== 'archive') {
                  e.target.style.background = '#f7fafc';
                  e.target.style.color = '#2d3748';
                }
              }}
              onMouseLeave={(e) => {
                if (tab !== 'archive') {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#4a5568';
                }
              }}
            >
              <MdArchive size={18} />
              Archive
            </button>
            <button
              onClick={() => setTab('recycle')}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                textAlign: 'left',
                padding: '12px 16px',
                borderRadius: 8,
                background: tab === 'recycle' ? 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: tab === 'recycle' ? 600 : 500,
                color: tab === 'recycle' ? '#fff' : '#4a5568',
                transition: 'all 0.2s ease',
                fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                boxShadow: tab === 'recycle' ? '0 4px 12px rgba(245, 87, 108, 0.4)' : 'none'
              }}
              onMouseEnter={(e) => {
                if (tab !== 'recycle') {
                  e.target.style.background = '#f7fafc';
                  e.target.style.color = '#2d3748';
                }
              }}
              onMouseLeave={(e) => {
                if (tab !== 'recycle') {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#4a5568';
                }
              }}
            >
              <MdDelete size={18} />
              Recycle bin
            </button>
            {/* Activity log and history removed per UI request */}
          </div>
        </aside>

        {/* Right content */}
        <section className="archived-posts-content" style={{ flex: 1 }}>
          <h2 className="archived-publication-title">{tab === 'archive' ? 'Archived Publication' : 'Trash'}</h2>

          <div style={{ marginTop: 12 }}>
            {tab === 'archive' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Small info banner above the Archive list */}
                <div className="archived-info-banner" style={{ background: '#eef0f2 ', padding: 10, borderRadius: 6, textAlign: 'center', color: '#333', fontSize: 13 }}>
                  Items in your archive are only visible to you.
                </div>
                {/* Top toolbar for archive (always visible) */}
                <div className="archived-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 12, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                  <div className="archived-bulk-selection" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedIds.length === posts.length && posts.length > 0} onChange={(e) => {
                        if (e.target.checked) setSelectedIds(posts.map(p => p._id)); else setSelectedIds([]);
                      }} />
                      <span style={{ fontWeight: 600 }}>All</span>
                    </label>
                    <div style={{ fontWeight: 700 }}>{selectedIds.length} selected</div>
                  </div>
                  <div className="archived-bulk-actions" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button onClick={async () => {
                      if (selectedIds.length === 0) return;
                      try {
                        await Promise.all(selectedIds.map(id => axios.put(`${API_URL}/archivePost/${id}`, { archive: false }, { headers: { Authorization: `Bearer ${token}` } })));
                        setPosts((prev) => prev.filter(p => !selectedIds.includes(p._id)));
                        setSelectedIds([]);
                      } catch (err) {
                        console.error(err);
                        alert('Failed to restore selected posts');
                      }
                    }} disabled={selectedIds.length === 0} style={{ padding: '8px 12px', borderRadius: 6, background: selectedIds.length === 0 ? '#f5f6f7' : '#fff', border: '1px solid #ccc', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}><IoMdUndo /> Restore</button>
                    <button onClick={async () => {
                      if (selectedIds.length === 0) return;
                      try {
                        await Promise.all(selectedIds.map(id => axios.delete(`${API_URL}/deletePost/${id}`, { headers: { Authorization: `Bearer ${token}` } })));
                        // move to recycle UI by removing from posts list
                        setPosts((prev) => prev.filter(p => !selectedIds.includes(p._id)));
                        setSelectedIds([]);
                      } catch (err) {
                        console.error(err);
                        alert('Failed to move to recycle bin');
                      }
                    }} disabled={selectedIds.length === 0} style={{ padding: '8px 12px', borderRadius: 6, background: selectedIds.length === 0 ? '#f5f6f7' : '#fff', border: '1px solid #ccc', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', gap: 8, alignItems: 'center' }}><FaTrashAlt /> Recycle bin</button>
                  </div>
                </div>

                {/* Group posts by date (simple same-day grouping) */}
                {posts.length === 0 && (<div style={{ color: '#666' }}>No archived posts found.</div>)}
                {posts.length > 0 && posts.map((post) => {
                  const created = post.createdAt ? new Date(post.createdAt) : new Date();
                  const dayLabel = created.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
                  const timeLabel = created.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
                  return (
                    <div key={post._id} className="archived-card" style={{ padding: 12, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                      <div className="archived-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                        <input type="checkbox" checked={selectedIds.includes(post._id)} onChange={(e) => { if (e.target.checked) setSelectedIds(prev => [...prev, post._id]); else setSelectedIds(prev => prev.filter(id => id !== post._id)); }} />
                        <img src={(post.user && post.user.picture) || '/default-avatar.png'} alt="avatar" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 700 }}>{(post.user && `${post.user.first_name} ${post.user.last_name}`) || post.username || 'Unknown'} <span style={{ color: '#333', fontWeight: 500 }}>added a new photo.</span></div>
                          <div style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
                            <span style={{ marginRight: 8 }}>📦 Archived {dayLabel}</span>
                          </div>
                        </div>
                        <div className="archived-card-actions" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ color: '#666', fontSize: 13 }}>{timeLabel}</div>
                          <button onClick={() => { window.location.href = `/post/${post._id}`; }} style={{ padding: '6px 10px', borderRadius: 6, background: '#e9edf2', border: 'none', cursor: 'pointer' }}>View</button>
                          <div className="archive-menu-wrapper">
                            <button className="archive_toggle" onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === post._id ? null : post._id); }} style={{ padding: '6px 10px', borderRadius: 20, background: '#fff', border: '1px solid #ddd', cursor: 'pointer' }}>⋯</button>
                            <div className="archive_menu" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', display: openMenuId === post._id ? 'block' : 'none', minWidth: 180 }}>
                              <button onClick={async () => {
                                try {
                                  await axios.put(`${API_URL}/archivePost/${post._id}`, { archive: false }, { headers: { Authorization: `Bearer ${token}` } });
                                  setPosts((prev) => prev.filter((p) => p._id !== post._id));
                                  setOpenMenuId(null);
                                } catch (err) {
                                  console.error(err);
                                  alert('Failed to restore');
                                }
                              }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 10, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                <IoMdUndo style={{ color: '#2c3e50' }} />
                                <span>Restore</span>
                              </button>
                              <button onClick={async () => {
                                try {
                                  await axios.delete(`${API_URL}/deletePost/${post._id}`, { headers: { Authorization: `Bearer ${token}` } });
                                  setPosts((prev) => prev.filter((p) => p._id !== post._id));
                                  setOpenMenuId(null);
                                } catch (err) {
                                  console.error(err);
                                  alert('Failed to move to recycle');
                                }
                              }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 10, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                                <FaTrashAlt style={{ color: '#c0392b' }} />
                                <span>Recycle bin</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {tab === 'recycle' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {/* Small info banner above the Recycle list */}
                <div style={{ background: '#eef0f2 ', padding: 10, borderRadius: 6, textAlign: 'center', color: '#333', fontSize: 13 }}>
                  Items in your Recycle bin are only visible to you.
                </div>
                {/* Bulk action toolbar (always shown, buttons disabled when no selection) */}
                <div className="archived-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', padding: 12, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                  <div className="archived-bulk-selection" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selectedIds.length === deletedPosts.length && deletedPosts.length > 0} onChange={(e) => {
                        if (e.target.checked) setSelectedIds(deletedPosts.map(p => p._id)); else setSelectedIds([]);
                      }} />
                      <span style={{ fontWeight: 600 }}>All</span>
                    </label>
                    <div style={{ fontWeight: 700 }}>{selectedIds.length} selected</div>
                  </div>
                  <div className="archived-bulk-actions" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <button onClick={() => setSelectedIds([])} disabled={selectedIds.length === 0} style={{ padding: '8px 12px', borderRadius: 6, background: selectedIds.length === 0 ? '#f5f6f7' : '#f0f2f5', border: '1px solid #ddd', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', color: selectedIds.length === 0 ? '#888' : 'inherit' }}>Deselect</button>
                    <button onClick={async () => {
                      if (selectedIds.length === 0) return;
                      try {
                        await Promise.all(selectedIds.map(id => axios.put(`${API_URL}/archivePost/${id}`, { archive: true }, { headers: { Authorization: `Bearer ${token}` } })));
                        setDeletedPosts((prev) => prev.filter(p => !selectedIds.includes(p._id)));
                        setSelectedIds([]);
                      } catch (err) {
                        console.error(err);
                        alert('Failed to move selected to archive');
                      }
                    }} disabled={selectedIds.length === 0} style={{ padding: '8px 12px', borderRadius: 6, background: selectedIds.length === 0 ? '#f5f6f7' : '#fff', border: '1px solid #ccc', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', gap: 8, alignItems: 'center', color: selectedIds.length === 0 ? '#888' : 'inherit' }}><FiArchive /> Archive</button>
                    <button onClick={async () => {
                      if (selectedIds.length === 0) return;
                      try {
                        await Promise.all(selectedIds.map(id => axios.put(`${API_URL}/restorePost/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } })));
                        setDeletedPosts((prev) => prev.filter(p => !selectedIds.includes(p._id)));
                        setSelectedIds([]);
                      } catch (err) {
                        console.error(err);
                        alert('Failed to restore selected posts');
                      }
                    }} disabled={selectedIds.length === 0} style={{ padding: '8px 12px', borderRadius: 6, background: selectedIds.length === 0 ? '#f5f6f7' : '#fff', border: '1px solid #ccc', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', gap: 8, alignItems: 'center', color: selectedIds.length === 0 ? '#888' : 'inherit' }}><IoMdUndo /> Restore</button>
                    <button onClick={() => { if (selectedIds.length === 0) return; setDeleteConfirm({ type: 'bulk', ids: [...selectedIds] }); }} disabled={selectedIds.length === 0} style={{ padding: '8px 12px', borderRadius: 6, background: selectedIds.length === 0 ? '#f5f6f7' : '#fff', border: '1px solid #f1c0c0', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', gap: 8, alignItems: 'center', color: selectedIds.length === 0 ? '#aaa' : '#a00' }}><FaTrashAlt /> Delete</button>
                  </div>
                </div>
                {deletedPosts.map((post) => (
                  <div key={post._id} className="archived-card" style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#fff', borderRadius: 8, boxShadow: '0 1px 4px rgba(0,0,0,0.03)' }}>
                    <div className="archived-card-body" style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%' }}>
                      <input type="checkbox" checked={selectedIds.includes(post._id)} onChange={(e) => { if (e.target.checked) setSelectedIds(prev => [...prev, post._id]); else setSelectedIds(prev => prev.filter(id => id !== post._id)); }} />
                      <img src={(post.user && post.user.picture) || '/default-avatar.png'} alt="avatar" style={{ width: 44, height: 44, borderRadius: '50%', objectFit: 'cover' }} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 700 }}>{(post.user && `${post.user.first_name} ${post.user.last_name}`) || post.username || 'Unknown'}</div>
                        <div style={{ color: '#333' }}>{post.text ? (post.text.length > 120 ? post.text.substring(0, 120) + '...' : post.text) : (post.title || '')}</div>
                        <div style={{ color: '#666', fontSize: 13, marginTop: 6 }}>
                          {(() => {
                            try {
                              const deletedAt = new Date(post.deletedAt);
                              const dateStr = deletedAt.toLocaleString();
                              // calculate days left from 30-day retention
                              const MS_PER_DAY = 1000 * 60 * 60 * 24;
                              const diffDays = Math.floor((now - deletedAt.getTime()) / MS_PER_DAY);
                              const rawLeft = 30 - diffDays;
                              const daysLeft = rawLeft > 0 ? rawLeft : 0;
                              let color = '#666';
                              // show warning red when 10 or fewer days left
                              if (daysLeft <= 10) color = '#c0392b';
                              return (
                                <div>
                                  <div>{dateStr}</div>
                                  <div style={{ color, fontSize: 12, marginTop: 4 }}>{daysLeft === 0 ? '0 days left' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`}</div>
                                </div>
                              );
                            } catch (e) {
                              return <div>Deleted</div>;
                            }
                          })()}
                        </div>
                      </div>
                    </div>

                    <div className="archived-card-actions" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button onClick={() => { window.location.href = `/post/${post._id}`; }} style={{ padding: '6px 10px', borderRadius: 6, background: '#e9edf2', border: 'none', cursor: 'pointer' }}>View</button>

                      <div className="archive-menu-wrapper">
                        <button className="recycle_toggle" onClick={(e) => { e.stopPropagation(); setOpenMenuId(openMenuId === post._id ? null : post._id); }} style={{ padding: '6px 10px', borderRadius: 20, background: '#fff', border: '1px solid #ddd', cursor: 'pointer' }}>⋯</button>
                        <div className="recycle_menu" onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid #ddd', borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', display: openMenuId === post._id ? 'block' : 'none', minWidth: 180 }}>
                          <button onClick={() => { setOpenMenuId(null); setDeleteConfirm({ type: 'single', post }); }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 10, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                            <FaTrashAlt style={{ color: '#c0392b' }} />
                            <span>Delete permanently</span>
                          </button>
                          <button onClick={async () => {
                            try {
                              await axios.put(`${API_URL}/restorePost/${post._id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              setDeletedPosts((prev) => prev.filter((p) => p._id !== post._id));
                              setOpenMenuId(null);
                            } catch (err) {
                              console.error(err);
                              alert('Failed to restore');
                            }
                          }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 10, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                            <IoMdUndo style={{ color: '#2c3e50' }} />
                            <span>Restore to profile</span>
                          </button>
                          <button onClick={async () => {
                            try {
                              await axios.put(`${API_URL}/archivePost/${post._id}`, { archive: true }, { headers: { Authorization: `Bearer ${token}` } });
                              setDeletedPosts((prev) => prev.filter((p) => p._id !== post._id));
                              setOpenMenuId(null);
                            } catch (err) {
                              console.error(err);
                              alert('Failed to move to archive');
                            }
                          }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: 10, textAlign: 'left', background: 'transparent', border: 'none', cursor: 'pointer' }}>
                            <FiArchive style={{ color: '#34495e' }} />
                            <span>Move to archive</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                {deletedPosts.length === 0 && (<div style={{ color: '#666' }}>No items in trash.</div>)}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
